import { motion } from 'framer-motion';
import { useState } from 'react';
import { Plus, Minus } from 'lucide-react';

const faqs = [
  {
    q: "Wait, this is actually healthy?",
    a: "Healthier than a regular cream biscuit, yes. 10g protein, 3g fibre, under 200 kcal per pack (vs ~500 in regular cream biscuits), no palm oil, no refined sugar. But let's be real — it's still a treat, not a salad. We just made the treat a lot less guilty."
  },
  {
    q: "Wait, under 200 calories for a whole pack? How?",
    a: "Regular cream biscuit packs hit ~500 kcal mostly from palm oil and refined sugar. We're swapping those out for higher-protein flours, real cocoa butter (in measured amounts), and natural sweeteners. Less of the calorie-dense junk = fewer calories without losing the cream biscuit feel. Final number gets locked at launch — we're targeting under 200 kcal per 100g pack."
  },
  {
    q: "When's it actually launching?",
    a: "We're in the final stretch. Join the waitlist and you'll be the first to know the exact drop date. Founding batch is limited — don't sleep on it."
  },
  {
    q: "Does it taste like cardboard like other 'healthy' biscuits?",
    a: "If it did, we wouldn't ship it. That's literally our promise. We're testing against the OGs (Oreo, Bourbon, Jim Jam) and if Way Big doesn't beat them blind, it goes back to the kitchen."
  },
  {
    q: "How much will it cost?",
    a: "Price isn't locked yet, but it'll be in line with a quality cream biscuit — not in 'protein bar pricing' territory. Founding members get a launch discount, FYI."
  },
  {
    q: "Why no palm oil? What's wrong with it?",
    a: "Two reasons. One: it's wrecking rainforests in Indonesia and Malaysia. Two: it's not great for you. Most cream biscuits use it because it's dirt cheap. We use real cocoa butter and other sustainable fats instead. Costs us more. Worth it."
  },
  {
    q: "Is this Oreo with a glow-up?",
    a: "We have a lot of respect for Oreo — it's the OG. But yeah, basically we read the ingredients, didn't love what we saw, and rebuilt it from scratch with stuff you can pronounce."
  },
  {
    q: "Can I get this in my city?",
    a: "Launching D2C across India through our website first. Blinkit and other quick commerce platforms are next. Sign up for the waitlist and we'll keep you posted on your city."
  },
  {
    q: "Are there kids' versions or smaller packs?",
    a: "100g packs to start. If demand says go smaller (or bigger), we'll listen. Drop us a DM on @go.waybig with what you want."
  },
];

const FaqItem = ({ q, a, index, isOpen, onToggle }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.4, delay: index * 0.05 }}
    className="border-b border-[#2A2A2A]"
  >
    <button
      data-testid={`faq-toggle-${index}`}
      onClick={onToggle}
      className="w-full flex justify-between items-center py-6 text-left group"
    >
      <h3 className={`font-['Bebas_Neue'] text-2xl sm:text-3xl uppercase tracking-tight transition-colors pr-4 ${
        isOpen ? 'text-[#FF3D00]' : 'text-white group-hover:text-[#FF3D00]'
      }`}>
        {q}
      </h3>
      <div className={`flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}>
        {isOpen ? (
          <Minus className="text-[#FF3D00]" size={28} />
        ) : (
          <Plus className="text-white group-hover:text-[#FF3D00] transition-colors" size={28} />
        )}
      </div>
    </button>
    <motion.div
      initial={false}
      animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }}
      transition={{ duration: 0.3 }}
      className="overflow-hidden"
    >
      <p className="pb-6 font-['Manrope'] text-base sm:text-lg text-white/70 leading-relaxed pr-12" data-testid={`faq-answer-${index}`}>
        {a}
      </p>
    </motion.div>
  </motion.div>
);

const FaqSection = () => {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section data-testid="faq-section" className="py-24 px-6 bg-[#0A0A0A] border-t border-[#2A2A2A]">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <div className="inline-block bg-[#FF3D00] text-black px-3 py-1 font-['Outfit'] font-bold uppercase text-xs tracking-[0.2em] mb-4">
            Real Talk
          </div>
          <h2 className="font-['Bebas_Neue'] text-5xl sm:text-6xl text-white uppercase leading-none" data-testid="faq-heading">
            Questions you're<br /><span className="text-[#FF3D00]">probably asking.</span>
          </h2>
        </motion.div>

        <div className="border-t border-[#2A2A2A]">
          {faqs.map((faq, index) => (
            <FaqItem
              key={index}
              {...faq}
              index={index}
              isOpen={openIndex === index}
              onToggle={() => setOpenIndex(openIndex === index ? -1 : index)}
            />
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <p className="font-['Manrope'] text-white/60 mb-2">Got more questions?</p>
          <a
            href="https://instagram.com/go.waybig"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="faq-instagram-link"
            className="font-['Bebas_Neue'] text-2xl text-[#FF3D00] hover:text-[#FFB300] tracking-wider"
          >
            DM us @go.waybig →
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default FaqSection;
